import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { mapToBase } from "go-core/api/searchSelectService";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import { getReportCustomFieldsResources, reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import ReportOrganizationFeatureUtils from "../Services/ReportOrganizationFeatureUtils";
import SynchronizeOrganizationFeatureUsageReportModal from "../components/SynchronizeOrganizationFeatureUsageReportModal";

interface State {
	resource?: Record<string, any>;
}

const listName = "ADMIN_TRANSACTION_REPORT";
const resourceType = "TRANSACTION_REPORT";
const columns = [
	{
		id: "potential_count",
		name: "Ilość - potencjalnych",
	},
	{
		id: "enabled_count",
		name: "Ilość - Aktywnych",
	},
	{
		id: "used_count",
		name: "Ilość - używanych",
	},
	{
		id: "potential_points",
		name: "Punkty - potencjalne",
	},
	{
		id: "enabled_points",
		name: "Punkty - Aktywne",
	},
	{
		id: "used_points",
		name: "Punkty - użyte",
	},
	{
		id: "last_usage_value",
		name: "Ilość zużycia (30 dni)",
	},
	{
		id: "subscription_count",
		name: "Subskrypcje",
	},
];

const Report = ({ resource }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRefresh = async () => {
		if (config.fetch) {
			await config.fetch({});
		}
	};

	let config = {
		reportConfigId: "admin_feature_report",
		aggregatePrefix: "summary",
		selectedColumns: columns.map((x) => x.id),
		groups: [
			...ReportOrganizationFeatureUtils.getFeatureGroups(t),
			...ReportOrganizationFeatureUtils.getOrganizationGroups(t, "ORGANIZATION"),
		],
		showPercent: false,
		groupsInfo: {
			ORGANIZATION_NAME: (groupByValue: Record<string, any>) => {
				const link = `/admin/organizations?search=${groupByValue.name}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
			ORGANIZATION_COMPANY_ID: (groupByValue: Record<string, any>) => {
				const link = `/admin/organizations?search=${groupByValue.name}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
		},
		columns: columns,
		selectedGroups: ["DATE"],
		filters: [
			...ReportOrganizationFeatureUtils.getFeatureFilters(t),
			...ReportOrganizationFeatureUtils.getOrganizationFilters(t, "organization"),
			...[
				{
					id: "product_subscription",
					name: "Produkt - subskrypcja",
					type: "boolean",
				},
			],
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: columns.map((x) => x.id),
				filters: [],
				groups: [],
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getOrganizationFeatureReport(reportParamsConverter(params), {
					cancelToken: sourceToken?.token,
				}),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.transaction_report.header.title"),
			filename: t("modules.transaction_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ORGANIZATION", "PARTNER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return (
		<>
			<ReportData config={config} />
		</>
	);
};

const AdminOrganizationFeatureReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const [syncModal, setSyncModal] = useState<boolean>(false);
	const { addFlash } = useFlash();

	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
	];
	const isMobile = useWindowSize().isMobile;

	const { handleChangeTabTitle } = useBrowserTabTitle();
	const buttons: ButtonProps[] = [
		{
			title: "Synchronizuj funkcje",
			action: () => setSyncModal(true),
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = buttons.map((x) => {
		return { title: x.title, action: x.action } as MobileActionProps;
	});

	useEffect(() => {
		handleInit();
	}, []);

	const handleInit = async () => {
		handleChangeTabTitle(
			`${t("modules.transaction_report.header.title")} | ${t("lib:go_component.navigation.admin")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	};

	return (
		<div className={"content"}>
			{!isMobile && <Header title={"Raport funkcji - użycie"} buttons={buttons} />}
			<MobileActions actions={mobileActions} />
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
				{syncModal && (
					<SynchronizeOrganizationFeatureUsageReportModal
						isShown={syncModal}
						onSuccessHandle={() => {}}
						onHide={() => setSyncModal(false)}
					/>
				)}
			</Suspense>
		</div>
	);
};

export default AdminOrganizationFeatureReportPage;
